import { Component, DOCUMENT, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterEvent } from '@angular/router';
import { Subscription, delay, filter, pairwise, tap } from 'rxjs';

import { Base64Service } from '@app/core/service/base64.service';
import { HeaderService } from '@app/shell/header/service/header/header.service';
import { ComboboxService } from '@app/tw-shared/directives';
import { ComboboxTypeaheadService } from '@app/tw-shared/directives/combobox-typeahead/combobox-typeahead.service';
import { DropdownOpenCloseService } from '@app/tw-shared/directives/dropdown-open-close/dropdown-open-close.service';
import { UserService } from '@app/user';
import { setGrow } from '../../animations/grow.animation';
import { SearchExpand } from '../../animations/search-expand.animation';
import { SearchExploreService } from '../../services/search-explore.service';
import { SearchService } from '../../services/search.service';
import { ResultsComponent } from '../results/results.component';
@Component({
    selector: 'app-search',
    templateUrl: './search.component.html',
    styleUrls: ['./search.component.scss'],
    animations: [SearchExpand, setGrow('0.4s', '0.1s')],
    providers: [
        ComboboxService,
        {
            provide: DropdownOpenCloseService,
            useFactory: () => new DropdownOpenCloseService('search'),
        },
        ComboboxTypeaheadService,
    ],
    standalone: false,
})
export class SearchComponent implements OnInit, OnDestroy {
    @ViewChild(ResultsComponent) resultsComponent: ResultsComponent;
    @ViewChild('searchInput') searchInputRef: ElementRef<HTMLInputElement>;

    private readonly userService = inject(UserService);
    private readonly headerService = inject(HeaderService);
    private readonly base64 = inject(Base64Service);
    private readonly router = inject(Router);
    private readonly searchExploreService = inject(SearchExploreService);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly dropdownOpenCloseService = inject(DropdownOpenCloseService);
    private readonly searchService = inject(SearchService);
    private readonly document = inject(DOCUMENT);

    private readonly subscriptions: Subscription = new Subscription();
    readonly isExplore$ = this.searchExploreService.isExplore$;
    readonly open$ = this.dropdownOpenCloseService.openEvent$.pipe(
        tap((open) => this.headerService.setSearchbarExpand(open)),
        delay(50),
    );
    readonly searchControl = this.searchExploreService.searchControl;
    readonly id = this.searchExploreService.resultsListId;

    get hasTrial() {
        return this.userService.user.company.trial;
    }

    get hasCustomLogo() {
        return this.userService.hasCoBrandingLogo;
    }

    ngOnInit() {
        this.subscriptions.add(
            this.open$
                .pipe(
                    pairwise(),
                    tap(([prev, curr]) => {
                        if (prev && !curr) {
                            this.searchInputRef.nativeElement.focus();
                            this.dropdownOpenCloseService.openEvent$.next(false);
                        }
                    }),
                )
                .subscribe(),
        );

        this.subscriptions.add(
            this.router.events
                .pipe(filter((event) => event instanceof NavigationEnd))
                .pipe(
                    tap((event) => {
                        const routerEvent = <RouterEvent>event;
                        if (!routerEvent.url.includes('/explore/')) {
                            this.searchControl.patchValue('');
                            this.searchExploreService.setSearch('');
                        }
                        this.dropdownOpenCloseService.openEvent$.next(false);
                    }),
                )
                .subscribe(),
        );

        this.restoreTermFormQueryParams();
    }

    open() {
        if (!this.document.querySelector('body').classList.contains('using-mouse')) {
            this.dropdownOpenCloseService.openEvent$.next(true);
        }
    }

    removeValue() {
        this.searchControl.patchValue('');
        if (!this.document.querySelector('body').classList.contains('using-mouse')) {
            this.searchInputRef.nativeElement.focus();
        }
    }

    ngOnDestroy() {
        this.headerService.setSearchbarExpand(false);
        this.subscriptions.unsubscribe();
    }

    closeSearch() {
        this.dropdownOpenCloseService.openEvent$.next(false);
    }

    onEnter() {
        this.closeSearch();
        this.searchService.search$.next(null);
    }

    private restoreTermFormQueryParams(): void {
        const options = this.pareseUrlOptions();
        if (options.term !== undefined) {
            this.searchControl.patchValue(options.term);
        }
    }

    private pareseUrlOptions() {
        try {
            return JSON.parse(this.base64.decode(this.activatedRoute.snapshot.queryParams['options']));
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (e) {
            return '';
        }
    }
}
